// @ts-nocheck
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { DocAndContracts } from "./_components/DocAndContracts";
import {
  getContractorAgreementState,
  getContractsForState,
} from "../../../../utils/methods/contract/contractMethods";
import { apiRequest } from "../../../../services/apiService";
import ContractorDocumentsCard from "../../../onboarding/ContractorDocumentsCard";

const AddContractorCongratulations = ({
  setCurrentStep,
  closeAndResetContractorFlowHandler,
  isEditing,
  isViewing = false,
  heading,
  subHeading,
  showContracts = true,
}) => {
  // State variables start...
  // State variables end...

  const auth = useSelector((state) => state.auth);
  const contractor = useSelector((store) => store.contractor);
  const { contractorPDFUrls } = useSelector((store) => store.contractorLink);

  // Redux hooks start...
  const dispatch = useDispatch();
  // Redux hooks end...

  // Selectors start...
  // Selectors end...

  // Form schema...
  // Form schema...

  // Handlers start...
  // Handlers end...

  const contractorFromStep1 = contractor?.addContractor?.step1?.data;
  const contractorIdFromStep1 = contractorFromStep1?._id;

  /**
   * 🔴 `step1.data` is the RAW ROW from the Contractors list
   * (`AddContractorModal.prefillEditContractorFlowHandler` does `data: contractor`),
   * and there `contractorAgreementId` is just the **ObjectId** — no list endpoint
   * resolves the document. `getContractorAgreementState` reads
   * `existingDocument[<key>].acceptances.at(-1).consent`; on a string that is always
   * `undefined`, so EVERY document rendered as "Not Accepted" (red X) with the text
   * `Updated On Feb 26, 2026` — which is the `CONTRACTOR_CONTRACT_UPDATED_AT`
   * constant, not a date belonging to the contractor.
   *
   * Symptom: the SAME person showed all 8 contracts accepted in
   * `DocAndContractsViewer` (Event → contact card → View Contracts, which calls
   * `getContractorAgreementsById`) and all red here, on the last step of the edit
   * wizard. Same bug already fixed in `ProfileContractor` (2026-08-03); this path
   * had been left behind.
   *
   * The route that returns the POPULATED agreement is `getContractorAgreementsById`
   * (`contractor-links.service#getById`) — the only one that resolves the pointer.
   */
  const [populatedAgreement, setPopulatedAgreement] = useState(null);

  useEffect(() => {
    setPopulatedAgreement(null);
    if (!showContracts || !contractorIdFromStep1) return;
    let cancelled = false;
    (async () => {
      const { response } = await apiRequest({
        method: "GET",
        url: "getContractorAgreementsById",
        params: { contractorId: contractorIdFromStep1 },
        // ⚠️ without `isToken: true` the `authorization` header is NOT sent
        // (apiService.ts:69) and the route, which has @Roles, answers 401.
        isToken: true,
      });
      if (cancelled) return;
      // on failure keep null: it falls back to the raw pointer and the screen
      // returns to the old behaviour ("Not Accepted") instead of breaking
      setPopulatedAgreement(response?.data?.data?.contractorAgreementId ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [contractorIdFromStep1, showContracts]);

  const currentDocument = useMemo(
    () =>
      getContractorAgreementState({
        contractorType: contractorFromStep1?.contractorType,
        contractorPDFUrls,
        // populated object when available; the list field is only the id
        existingDocument:
          populatedAgreement ?? contractorFromStep1?.contractorAgreementId,
      }),
    [
      contractorFromStep1?.contractorType,
      contractorFromStep1?.contractorAgreementId,
      contractorPDFUrls,
      populatedAgreement,
    ],
  );

  // useEffects start...
  // useEffects end...

  return (
    <>
      <Box
        sx={{
          height: contractorIdFromStep1 ? "auto" : "100%",
          minHeight: 0,
          flexShrink: contractorIdFromStep1 ? 0 : 1,
        }}
        className={"custom-scroll-design"}
      >
        {showContracts ? (
          <>
            <DocAndContracts
              readOnly
              isParentWraperEnabled={false}
              contractorId={contractorIdFromStep1}
              currentDocument={currentDocument}
              removePadding
            />
            {contractorIdFromStep1 && <ContractorDocumentsCard contractorId={contractorIdFromStep1} embedded />}
            <Box
              className="bottom-btn-box"
              sx={{
                width: "100%",
                paddingY: isViewing ? "1.25rem" : "2rem",
                justifyContent: isViewing ? "flex-end" : undefined,
                alignSelf: "flex-end",
              }}
            >
              <Button
                className="btn-black"
                sx={{
                  width: isViewing ? "auto" : "50%",
                  minWidth: isViewing ? "7rem" : undefined,
                  height: isViewing ? "2.75rem" : "4.25rem",
                  textTransform: "none !important",
                }}
                onClick={() => {
                  setCurrentStep((currentStep) => {
                    return currentStep - 1;
                  });
                }}
              >
                <Typography className="btn-text">Back</Typography>
              </Button>
              <Button
                className="btn-pink"
                sx={{
                  width: isViewing ? "auto" : "50%",
                  minWidth: isViewing ? "7rem" : undefined,
                  height: isViewing ? "2.75rem" : "4.25rem",
                  textTransform: "none !important",
                  "&:disabled": {
                    background: "#d85ad8 !important",
                  },
                }}
                onClick={() => {
                  closeAndResetContractorFlowHandler();
                }}
              >
                <Typography className="btn-text">{isViewing ? "Close" : "Continue"}</Typography>
              </Button>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: contractorIdFromStep1 ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {!isViewing && <Typography
              variant="h3"
              style={{
                textAlign: "center",
                fontWeight: "400",
                marginBottom: isViewing ? "1rem" : "3rem",
              }}
            >
              {isEditing
                ? heading || "Profile edited successfully!"
                : "Profile created successfully!"}
            </Typography>}

            {subHeading && subHeading}

            {contractorIdFromStep1 && <Box sx={{width:'100%'}}><ContractorDocumentsCard contractorId={contractorIdFromStep1} embedded /></Box>}

            {!isEditing && !isViewing ? (
              <>
                <Typography
                  variant="p"
                  sx={{
                    fontWeight: "200",
                    fontSize: "1.85rem",
                    lineHeight: "3rem",
                    textAlign: "center",
                  }}
                >
                  A notification email has been sent to this contractor. Please
                  continue with Other onboarding process.
                </Typography>
              </>
            ) : null}
          </Box>
        )}
      </Box>

      {!showContracts && (
        <Box
          className="bottom-btn-box"
          sx={{
            width: "100%",
            marginTop: "auto",
            paddingTop: isViewing ? "1.25rem" : undefined,
            justifyContent: isViewing ? "flex-end" : undefined,
            alignSelf: "flex-end",
            flexShrink: 0,
          }}
        >
          <Button
            className="btn-black"
            sx={{
              width: isViewing ? "auto" : "50%",
              minWidth: isViewing ? "7rem" : undefined,
              height: isViewing ? "2.75rem" : "4.25rem",
              textTransform: "none !important",
            }}
            onClick={() => {
              setCurrentStep((currentStep) => {
                return currentStep - 1;
              });
            }}
          >
            <Typography className="btn-text">Back</Typography>
          </Button>
          <Button
            className="btn-pink"
            sx={{
              width: isViewing ? "auto" : "50%",
              minWidth: isViewing ? "7rem" : undefined,
              height: isViewing ? "2.75rem" : "4.25rem",
              textTransform: "none !important",
              "&:disabled": {
                background: "#d85ad8 !important",
              },
            }}
            onClick={() => {
              closeAndResetContractorFlowHandler();
            }}
          >
            <Typography className="btn-text">{isViewing ? "Close" : "Continue"}</Typography>
          </Button>
        </Box>
      )}
    </>
  );
};

AddContractorCongratulations.propTypes = {
  currentStep: PropTypes.number,
  setCurrentStep: PropTypes.func,
  closeAndResetContractorFlowHandler: PropTypes.func,
  isEditing: PropTypes.bool,
  isViewing: PropTypes.bool,
  heading: PropTypes.string,
  subHeading: PropTypes.node || null,
};

export default AddContractorCongratulations;
